import { Component, OnInit, inject } from '@angular/core';
import { Background } from '../../shared/components/background/background';
import { Connect } from '../../shared/components/connect/connect';
import { HeroSection } from '../../shared/components/hero-section/hero-section';
import { Button } from '../../shared/components/button/button';
import { LeadSection } from '../landing/components/lead-section/lead-section';
import { TagServices } from '../landing/components/tag-services/tag-services';
import { StepsSection } from '../landing/components/steps-section/steps-section';
import { DevServices } from '../landing/components/dev-services/dev-services';
import { IconSection } from '../landing/components/icon-section/icon-section';
import { RevealDirective } from '../../shared/directives/reveal.directive';
import { SpotlightDirective } from '../../shared/directives/spotlight.directive';
import { CalendlyService } from '../../shared/services/calendly.service';
import { SeoService } from '../../shared/services/seo.service';
import {
  MARKETING_SECTIONS_INTRO,
  MARKETING_SECTIONS_ACQUISITION,
  MARKETING_SECTIONS_GROWTH,
} from '../../shared/data/app-marketing-sections';

const FAQS: { q: string; a: string }[] = [
  {
    q: 'What is included in your mobile app marketing services?',
    a: 'Our mobile app marketing services combine app store optimization (ASO), user acquisition, mobile advertising campaign management, influencer marketing, App Store conversion rate optimization, and retention and lifecycle marketing into a single, unified growth strategy for iOS and Android apps.',
  },
  {
    q: 'What is App Store Optimization (ASO) and why does it matter?',
    a: 'ASO is the process of improving an app’s visibility and conversion rate in the Apple App Store and Google Play. It covers keyword research, metadata and title optimization, screenshots, preview videos, and A/B testing. Strong ASO increases organic downloads and lowers customer acquisition costs because the growth compounds over time.',
  },
  {
    q: 'Which advertising platforms do you manage for app campaigns?',
    a: 'We manage paid user acquisition across Apple Search Ads, Google App Campaigns, Facebook and Instagram Ads, TikTok Ads, Reddit Ads, and YouTube Ads. We continuously test creative, audiences, bidding strategies, and conversion funnels to drive the highest-quality installs.',
  },
  {
    q: 'How do you lower customer acquisition cost (CAC) and improve ROAS?',
    a: 'We take a revenue-first approach: rather than optimizing only for installs, we optimize toward CAC, return on ad spend (ROAS), subscriber growth, retention, and lifetime value (LTV). Combining ASO with App Store conversion rate optimization also reduces the cost of every paid and organic visitor.',
  },
  {
    q: 'How do you improve app retention and lifetime value?',
    a: 'Our growth marketing team improves onboarding, engagement, and churn through push notification strategies, email automation, user segmentation, re-engagement campaigns, subscription optimization, in-app messaging, and behavioral analytics. Improving retention often generates larger revenue gains than acquiring additional users.',
  },
];

@Component({
  selector: 'app-marketing-services',
  imports: [Background, HeroSection, Button, Connect, LeadSection, TagServices, StepsSection, DevServices, IconSection, RevealDirective, SpotlightDirective],
  templateUrl: './marketing-services.html',
  styleUrl: './marketing-services.scss'
})
export class MarketingServices implements OnInit {
  protected file = 'marketing-services';
  /** Hero heading — "mobile app" in the brand gradient, "marketing" in white. */
  protected heroTitleHtml = '<span class="gradient-text">mobile app</span><br>marketing<br><span class="gradient-text">services</span>';

  // Section 0 ("full-service mobile app marketing agency") renders as a split header
  // (lead-section); the rest keep the generic centered content-section.
  protected introA = MARKETING_SECTIONS_INTRO[0];
  // Section 1 ("app store optimization services") renders as a split header + check-pill tags.
  protected asoSection = MARKETING_SECTIONS_INTRO[1];
  // "mobile app user acquisition" renders as a split header + check-pill tags; the rest
  // (advertising, influencer) keep the generic centered content-section.
  protected userAcquisitionSection = MARKETING_SECTIONS_ACQUISITION[0];
  // "mobile app advertising campaign management" renders as a split header + ad-platform icon cards.
  protected advertisingSection = MARKETING_SECTIONS_ACQUISITION[1];
  protected adCards = [
    { icon: 'apple-search-ads.png',    label: 'apple search ads' },
    { icon: 'google-app-campaigns.png', label: 'google app campaigns' },
    { icon: 'facebook-ads.png',        label: 'facebook ads' },
    { icon: 'instagram-ads.png',       label: 'instagram ads' },
    { icon: 'tiktok-ads.png',          label: 'tiktok ads' },
    { icon: 'reddit-ads.png',          label: 'reddit ads' },
    { icon: 'youtube-ads.png',         label: 'youtube ads' },
  ];
  // "influencer marketing for mobile apps" renders as an icon section (camera icon left, text right).
  protected influencerSection = MARKETING_SECTIONS_ACQUISITION[2];
  // "app store conversion rate optimization" renders as a split header + check-pill tags;
  // the rest (retention, why-choose) keep the generic centered content-section.
  protected croSection = MARKETING_SECTIONS_GROWTH[0];
  // "retention & growth marketing" renders as heading/intro left + stacked label cards right.
  protected retentionSection = MARKETING_SECTIONS_GROWTH[1];
  // "why businesses choose semnexus" renders centered (lead-section).
  protected whyChooseSection = MARKETING_SECTIONS_GROWTH[2];

  private calendly = inject(CalendlyService);
  private seo = inject(SeoService);

  ngOnInit() {
    this.seo.apply({
      title: 'Mobile App Marketing Services',
      description: 'Full-service mobile app marketing agency: app store optimization (ASO), user acquisition, mobile advertising, influencer marketing, App Store conversion rate optimization, and retention marketing built to grow downloads, engagement, and revenue.',
      canonical: '/mobile-app-marketing-services/',
      image: 'https://semnexus.com/images/marketing-services/backgrounds/marketing-services.avif',
      imageAlt: 'Mobile app marketing services by SEM Nexus',
      type: 'website',
    });
    this.seo.setBreadcrumbs([
      { name: 'Home', url: '/' },
      { name: 'App Marketing' },
    ]);
    this.seo.setJsonLd('service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://semnexus.com/mobile-app-marketing-services/#service',
      name: 'Mobile App Marketing',
      serviceType: 'ASO, user acquisition, mobile advertising, influencer, CRO, and retention marketing',
      description: 'Full-service mobile app marketing: App Store Optimization, paid user acquisition across social/search/display, mobile advertising campaign management, influencer marketing, App Store conversion rate optimization, and retention/lifecycle marketing for iOS and Android apps.',
      provider: { '@id': 'https://semnexus.com/#organization' },
      areaServed: { '@type': 'Country', name: 'United States' },
      url: 'https://semnexus.com/mobile-app-marketing-services/',
      offers: {
        '@type': 'OfferCatalog',
        name: 'Mobile app marketing services',
        itemListElement: [
          { '@type': 'Offer', name: 'App Store Optimization (ASO)', description: 'Keyword research, metadata, title, screenshot, and preview video optimization plus App Store A/B testing across the Apple App Store and Google Play.' },
          { '@type': 'Offer', name: 'Mobile App User Acquisition', description: 'Paid user acquisition across Apple Search Ads, Google Ads, Meta, TikTok, and Reddit optimized for CAC, ROAS, and lifetime value.' },
          { '@type': 'Offer', name: 'Mobile App Advertising Campaign Management', description: 'Creative testing, audience segmentation, bidding, and funnel optimization across Apple Search Ads, Google App Campaigns, Facebook, Instagram, TikTok, Reddit, and YouTube.' },
          { '@type': 'Offer', name: 'Influencer Marketing for Mobile Apps', description: 'Creator identification, outreach, negotiation, content strategy, tracking, and reporting to drive trust, social proof, and organic awareness.' },
          { '@type': 'Offer', name: 'App Store Conversion Rate Optimization', description: 'Optimization of app icons, screenshots, feature graphics, preview videos, value propositions, and messaging to lift App Store conversion rates.' },
          { '@type': 'Offer', name: 'Retention & Growth Marketing', description: 'Onboarding, push notifications, email automation, segmentation, re-engagement, subscription optimization, and behavioral analytics to maximize LTV.' },
        ],
      },
    });
    this.seo.setJsonLd('faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': 'https://semnexus.com/mobile-app-marketing-services/#faq',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  scheduleCall() {
    this.calendly.openPopup().catch(() => { });
  }
}
